import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAudioRecorder } from "../../hooks/useAudioRecorder";
import {
  AttachmentType,
  ConfirmAttachmentItem,
  UploadUrlFile,
  UploadUrlsResponse,
  uploadBlobToS3,
} from "../../services/supportTicket.service";

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

export interface StagedAttachment {
  id:              string;
  attachment_type: AttachmentType;
  file_name:       string;
  mime_type:       string;
  blob:            Blob;
}

const LABELS: Record<AttachmentType, string> = {
  voice_note: "Voice note",
  photo: "Photo",
  screen_recording: "Screen recording",
};

function typeFromMime(mime: string): AttachmentType | null {
  if (mime.startsWith("image/")) return "photo";
  if (mime.startsWith("audio/")) return "voice_note";
  if (mime.startsWith("video/")) return "screen_recording";
  return null;
}

/** Upload staged files straight to S3 and return the confirm payload for the API. */
export async function uploadStaged(
  staged: StagedAttachment[],
  requestUploadUrls: (files: UploadUrlFile[]) => Promise<UploadUrlsResponse>,
): Promise<ConfirmAttachmentItem[]> {
  if (staged.length === 0) return [];
  const { files } = await requestUploadUrls(
    staged.map(p => ({ attachment_type: p.attachment_type, file_name: p.file_name, mime_type: p.mime_type, file_size_bytes: p.blob.size })),
  );
  await Promise.all(files.map((item, i) => (item.upload_url ? uploadBlobToS3(item.upload_url, staged[i].blob, staged[i].mime_type) : Promise.resolve())));
  return files.map((item, i) => ({
    s3_key: item.s3_key,
    attachment_type: item.attachment_type,
    file_name: staged[i].file_name,
    mime_type: staged[i].mime_type,
    file_size_bytes: staged[i].blob.size,
  }));
}

interface Props {
  value: StagedAttachment[];
  onChange: (next: StagedAttachment[]) => void;
  /** How many files the staged list may hold in total. */
  slots: number;
}

/** Controlled attach-file / voice-note / screen-recording picker with previews. */
export const AttachmentStager: React.FC<Props> = ({ value, onChange, slots }) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const screenRecorderRef = useRef<MediaRecorder | null>(null);
  const screenChunksRef = useRef<Blob[]>([]);
  const [isRecordingScreen, setIsRecordingScreen] = useState(false);
  const { isRecording, duration, startRecording, stopRecording } = useAudioRecorder();

  // `value` can change under async recorder callbacks — read it through a ref.
  const valueRef = useRef(value);
  valueRef.current = value;
  const slotsRef = useRef(slots);
  slotsRef.current = slots;

  const previews = useRef<Map<string, string>>(new Map());
  const previewFor = (p: StagedAttachment) => {
    let url = previews.current.get(p.id);
    if (!url) { url = URL.createObjectURL(p.blob); previews.current.set(p.id, url); }
    return url;
  };
  useEffect(() => () => { previews.current.forEach(u => URL.revokeObjectURL(u)); }, []);

  const add = (p: StagedAttachment) => {
    if (valueRef.current.length >= slotsRef.current) {
      toast.error("This ticket can have at most 5 attachments in total.");
      return;
    }
    if (p.blob.size > MAX_FILE_SIZE_BYTES) {
      toast.error("That file is larger than the 50 MB limit.");
      return;
    }
    onChange([...valueRef.current, p]);
  };

  const remove = (id: string) => {
    const u = previews.current.get(id);
    if (u) { URL.revokeObjectURL(u); previews.current.delete(id); }
    onChange(valueRef.current.filter(p => p.id !== id));
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const t = typeFromMime(file.type);
    if (!t) { toast.error("Only photos, audio and video files can be attached."); return; }
    add({ id: crypto.randomUUID(), attachment_type: t, file_name: file.name, mime_type: file.type, blob: file });
  };

  const onVoice = async () => {
    if (isRecording) {
      const blob = await stopRecording();
      if (blob) add({ id: crypto.randomUUID(), attachment_type: "voice_note", file_name: `voice-note-${Date.now()}.webm`, mime_type: "audio/webm", blob });
      return;
    }
    try { await startRecording(); } catch { toast.error("Microphone access is required to record a voice note."); }
  };

  const onScreen = async () => {
    if (isRecordingScreen) { screenRecorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      screenRecorderRef.current = recorder;
      screenChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) screenChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(screenChunksRef.current, { type: "video/webm" });
        screenChunksRef.current = [];
        stream.getTracks().forEach(t => t.stop());
        setIsRecordingScreen(false);
        add({ id: crypto.randomUUID(), attachment_type: "screen_recording", file_name: `screen-recording-${Date.now()}.webm`, mime_type: "video/webm", blob });
      };
      stream.getVideoTracks()[0].addEventListener("ended", () => recorder.stop());
      recorder.start();
      setIsRecordingScreen(true);
    } catch {
      toast.error("Screen recording was not started (permission denied or unsupported browser).");
    }
  };

  const chip = "inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium";

  return (
    <div>
      {value.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {value.map(p => (
            <li key={p.id} className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
              <div className="mb-1 flex items-center justify-between">
                <span className="truncate">New · {LABELS[p.attachment_type]} · {p.file_name}</span>
                <button type="button" onClick={() => remove(p.id)} className="ml-2 flex-shrink-0 text-gray-400 hover:text-red-600">Remove</button>
              </div>
              {p.attachment_type === "photo" && <img src={previewFor(p)} alt="" className="h-20 w-auto rounded border border-gray-200" />}
              {p.attachment_type === "voice_note" && <audio controls src={previewFor(p)} className="h-8 w-full max-w-xs" />}
              {p.attachment_type === "screen_recording" && <video controls src={previewFor(p)} className="max-h-32 rounded" />}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => fileRef.current?.click()} className={`${chip} border-gray-300 text-gray-700 hover:bg-gray-50`}>📎 Attach file</button>
        <input ref={fileRef} type="file" accept="image/*,audio/*,video/*" onChange={onFilePicked} className="hidden" />
        <button type="button" onClick={onVoice} className={`${chip} ${isRecording ? "border-red-300 bg-red-50 text-red-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
          {isRecording ? `● ${duration}s — stop` : "🎤 Voice note"}
        </button>
        <button type="button" onClick={onScreen} className={`${chip} ${isRecordingScreen ? "border-red-300 bg-red-50 text-red-700" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>
          {isRecordingScreen ? "● Recording — stop" : "🖥 Record screen"}
        </button>
      </div>
    </div>
  );
};
